import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { DbLeadCreate, DbLeadUpdate } from "@/types/database";
import { toUuidOrNull } from "@/lib/uuid";
import { SystemUpdateService } from '@/services/system-update.service';

export type LeadPatch = DbLeadUpdate;
const LEADS_CACHE_TTL_MS = 30_000;
const LEAD_SELECT_COLUMNS = [
  "id",
  "organization_id",
  "customer_id",
  "source_id",
  "provider",
  "source_name",
  "primary_name",
  "secondary_name",
  "primary_phone",
  "secondary_phone",
  "primary_email",
  "secondary_email",
  "address",
  "city",
  "state",
  "zip",
  "project_type",
  "lead_source",
  "lead_source_id",
  "lead_cost",
  "stage",
  "appointment_time",
  "assigned_rep",
  "ownership_history",
  "activities",
  "stage_history",
  "linked_quote_id",
  "linked_job_id",
  "notes",
  "created_at",
  "updated_at",
  "branch_id",
  "decision_status",
  "validation_status",
  "rejection_reason",
  "rejection_notes",
  "accepted_at",
  "rejected_at",
  "received_at",
  "is_duplicate",
  "matched_duplicate_by",
  "status",
].join(", ");
let leadsAllCache: { at: number; data: unknown; error: unknown } | null = null;
let leadsAllInFlight: Promise<{ data: unknown; error: unknown }> | null = null;

function invalidateLeadCaches() {
  leadsAllCache = null;
  leadsAllInFlight = null;
}

function splitLeadName(fullName: string): { first_name: string | null; last_name: string | null } {
  const trimmed = fullName.trim();
  if (!trimmed) return { first_name: null, last_name: null };

  const [firstName, ...rest] = trimmed.split(/\s+/);
  return {
    first_name: firstName || null,
    last_name: rest.length ? rest.join(" ") : null,
  };
}

async function getCurrentUserIdOrNull(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

/**
 * LeadService provides data access to the leads table in Supabase
 * This serves as the "customer" data source in the authenticated system
 */
export const LeadService = {
  /**
   * Get all leads from Supabase - mimics Supabase's return format { data, error }
   */
  async getAll(options?: { forceRefresh?: boolean }) {
    const forceRefresh = options?.forceRefresh === true;
    if (forceRefresh) {
      invalidateLeadCaches();
    }
    const now = Date.now();
    if (!forceRefresh && leadsAllCache && now - leadsAllCache.at < LEADS_CACHE_TTL_MS) {
      return { data: leadsAllCache.data, error: leadsAllCache.error };
    }
    if (!forceRefresh && leadsAllInFlight) {
      const inFlight = await leadsAllInFlight;
      return { data: inFlight.data, error: inFlight.error };
    }

    const request = (async () => {
    const { data, error } = await supabase
      .from("leads")
      .select(LEAD_SELECT_COLUMNS)
      .order("created_at", { ascending: false });

      leadsAllCache = { at: Date.now(), data, error };
    return { data, error };
    })();
    leadsAllInFlight = request;
    try {
      return await request;
    } finally {
      leadsAllInFlight = null;
    }
  },

  /**
   * Get leads by stage/status
   */
  async getByStage(stage: string) {
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .eq("stage", stage)
      .order("created_at", { ascending: false });

    return { data, error };
  },

  /**
   * Get a single lead by ID
   */
  async getById(id: string) {
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    return { data, error };
  },

  /**
   * Search leads by name or address
   */
  async search(query: string) {
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .or(`primary_name.ilike.%${query}%,address.ilike.%${query}%,city.ilike.%${query}%`)
      .order("created_at", { ascending: false });

    return { data, error };
  },

  /**
   * Create a new lead - mimics Supabase's return format { data, error }
   * Auto-populates created_by from auth context if not provided
   */
  async create(leadData: Partial<DbLeadCreate> & {
    primary_name: string;
    primary_phone: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    project_type: string;
    lead_source?: string | null;
    provider?: string;
    lead_source_id?: string | null;
    stage?: string;
    assigned_rep?: string | null;
    notes?: string | null;
    organization_id?: string;
  }) {
    const organizationId = leadData.organization_id || await getCurrentOrganizationId();
    const { first_name, last_name } = splitLeadName(leadData.primary_name);
    const currentUserId = await getCurrentUserIdOrNull();
    const assignedRepId =
      leadData.assigned_rep === undefined
        ? currentUserId ?? null
        : toUuidOrNull(leadData.assigned_rep);

    const { data, error } = await supabase
      .from("leads")
      .insert([{
        ...leadData,
        assigned_rep: assignedRepId,
        organization_id: organizationId,
        provider: leadData.provider || "manual",
        source_name: leadData.lead_source || null,
        first_name,
        last_name,
        phone: leadData.primary_phone,
        email: leadData.primary_email || null,
        address_line1: leadData.address,
        postal_code: leadData.zip,
      }])
      .select()
      .single();

    if (!error) {
      invalidateLeadCaches();
      void SystemUpdateService.post({
        domain: 'pipeline',
        event:  'lead_created',
        body:   `System: Lead Created — ${leadData.primary_name}`,
      });
    }
    return { data, error };
  },

  /**
   * Update an existing lead
   */
  async update(id: string, updates: LeadPatch) {
    const normalizedUpdates: Record<string, unknown> = { ...updates };
    if (Object.prototype.hasOwnProperty.call(updates, "assigned_rep")) {
      normalizedUpdates.assigned_rep = toUuidOrNull(updates.assigned_rep as string | null | undefined);
    }

    const { data, error } = await supabase
      .from("leads")
      .update(normalizedUpdates)
      .eq("id", id)
      .select()
      .maybeSingle();

    if (!error) {
      invalidateLeadCaches();
      const opportunityStages = ['Quote Created', 'Closed Won', 'Appointment Scheduled', 'Appointment Completed'];
      if (updates.stage && opportunityStages.includes(updates.stage as string)) {
        void SystemUpdateService.post({
          domain:   'pipeline',
          event:    'lead_converted',
          body:     `System: Lead Converted to Opportunity`,
          metadata: {
            title:  'Lead Converted to Opportunity',
            detail: `Lead #${id}`,
            action: { label: 'View Opportunity', path: `/opportunities` },
          },
        });
      }
    }
    return { data, error };
  },

  /**
   * Create a new lead with an associated customer atomically via RPC
   * This creates both customer and lead records in a single transaction
   */
  async createWithCustomer(leadData: Partial<DbLeadCreate> & {
    primary_name: string;
    primary_phone: string;
    primary_email: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    project_type?: string;
    lead_source?: string | null;
    provider?: string;
    lead_source_id?: string | null;
    stage?: string;
    assigned_rep?: string | null;
    notes?: string | null;
    organization_id?: string;
  }) {
    const organizationId = leadData.organization_id || await getCurrentOrganizationId();
    const currentUserId = await getCurrentUserIdOrNull();
    const assignedRepId =
      leadData.assigned_rep === undefined
        ? currentUserId ?? null
        : toUuidOrNull(leadData.assigned_rep);

    const { data, error } = await supabase.rpc('intake_create_customer_and_lead_v2', {
      p_primary_name: leadData.primary_name,
      p_primary_phone: leadData.primary_phone,
      p_primary_email: leadData.primary_email,
      p_secondary_name: leadData.secondary_name,
      p_secondary_phone: leadData.secondary_phone,
      p_secondary_email: leadData.secondary_email,
      p_address: leadData.address || '',
      p_city: leadData.city || '',
      p_state: leadData.state || 'FL',
      p_zip: leadData.zip || '',
      p_project_type: leadData.project_type || 'Other',
      p_lead_source: leadData.lead_source || '',
      p_assigned_rep: assignedRepId,
      p_notes: leadData.notes,
      p_organization_id: organizationId,
      p_provider: leadData.provider || 'manual',
    });

    if (!error) {
      invalidateLeadCaches();
      void SystemUpdateService.post({
        domain: 'pipeline',
        event:  'lead_created',
        body:   `System: Lead Created — ${leadData.primary_name}`,
      });
    }
    return { data, error };
  },

  /**
   * Delete a lead
   */
  async delete(id: string) {
    const { error } = await supabase
      .from("leads")
      .delete()
      .eq("id", id);

    if (!error) invalidateLeadCaches();
    return { error };
  },

  /**
   * Get leads by branch
   */
  async getByBranch(branchId: string) {
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .eq("branch_id", branchId)
      .order("created_at", { ascending: false });

    return { data, error };
  },

  /**
   * Get leads assigned to a specific rep
   */
  async getByRep(assignedRep: string) {
    const assignedRepId = toUuidOrNull(assignedRep);
    if (!assignedRepId) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .eq("assigned_rep", assignedRepId)
      .order("created_at", { ascending: false });

    return { data, error };
  },

  /**
   * Get the lead linked to a specific job.
   * Primary: leads.linked_job_id = jobId
   * Fallback: leads.customer_id = job's customer_id (for older jobs without linked_job_id set)
   */
  async getByJobId(jobId: string, fallbackCustomerId?: string | null) {
    const { data, error } = await supabase
      .from("leads")
      .select(LEAD_SELECT_COLUMNS)
      .eq("linked_job_id", jobId)
      .limit(1)
      .maybeSingle();

    if (!error && data) return { data, error };

    // Fallback: match by customer_id, pick the most recently updated lead
    if (fallbackCustomerId) {
      const fallback = await supabase
        .from("leads")
        .select(LEAD_SELECT_COLUMNS)
        .eq("customer_id", fallbackCustomerId)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return { data: fallback.data, error: fallback.error };
    }

    return { data, error };
  },

  /**
   * Persist a lead event for timeline/audit history.
   */
  async addEvent(input: {
    leadId: string;
    customerId?: string | null;
    eventType: string;
    eventData?: Record<string, unknown>;
  }) {
    const organizationId = await getCurrentOrganizationId();
    const { data, error } = await supabase
      .from("lead_events")
      .insert([{
        organization_id: organizationId,
        lead_id: input.leadId,
        customer_id: input.customerId ?? null,
        event_type: input.eventType,
        event_data: input.eventData ?? {},
      }])
      .select()
      .single();

    return { data, error };
  },
};
